import React, { useEffect, useRef, useState } from 'react';
import logo from '../assets/branding/logo-rounded-1024.png';
import { ocultarSplashNativo } from '../lib/splashNativo';

// ── Tiempos del arranque (F4.3b iteración 2, spec de Carlos) ────────────────
//
// BOOT_SPLASH_MIN_MS — dwell mínimo del logo. DECISIÓN DELIBERADA DE PRODUCTO:
// el boot real puede resolver en <100ms y el splash "flasheaba" hacia signup,
// lo que se siente barato. Este mínimo es la ÚNICA excepción a la regla
// anti-esperas del repo, acotada al arranque: NO la "optimices" de vuelta —
// quitar estos ms no hace la app más rápida, la hace sentirse rota.
//
// El dwell es CONCURRENTE con el boot real, jamás aditivo:
//   total visible = max(boot real, BOOT_SPLASH_MIN_MS)
// (si el boot tarda 2s, el logo se va en cuanto el boot termina — no se suman
// 1.4s encima; el mínimo solo rellena cuando el boot fue más rápido que él).
export const BOOT_SPLASH_MIN_MS = 1400;

// Duración del fade-out hacia la primera pantalla real (ease-in-out — suave de
// verdad, no el corte de 300ms de la iteración 1). El fade NUNCA retrasa la
// interactividad: el overlay es pointer-events-none siempre, la pantalla
// destino recibe taps desde el primer frame en que está pintada debajo.
export const BOOT_FADE_MS = 600;

// El MISMO fondo que el splash nativo (app-assets/generated/splash.png,
// compuesto por scripts/generate-splash-source.js). Nativo → SPA debe leerse
// como UN solo momento: mismo logo, mismo #111111, sin costura visible.
export const BOOT_BACKGROUND = '#111111';

/**
 * BootSplash — la pantalla de arranque del SPA. Sustituye al spinner del gate
 * de loading de auth (regla F4.3b: cero spinner en todo el arranque; o pantalla
 * correcta, o logo). Aplica igual con sesión (logo→panel) y sin sesión
 * (logo→signup), en app y en web autenticada — el que decide es AuthBootGate.
 *
 * Fases: shown (opaco) → fading (fade + micro-escala del logo a 1.02, con
 * gusto y nada más) → hidden (desmontado). Al desactivarse `active`, el
 * overlay espera lo que falte del dwell mínimo (ver arriba: concurrente, no
 * aditivo) y entonces arranca el fade.
 *
 * src/assets/branding/logo-rounded-1024.png es una copia del PNG canónico en
 * app-assets/branding/ — CRA prohíbe importar fuera de src/, y app-assets/
 * debe seguir existiendo como fuente para @capacitor/assets. Si el logo
 * cambia, cambian ambos.
 */
export default function BootSplash({ active }) {
  const [phase, setPhase] = useState(active ? 'shown' : 'hidden');
  // Primer paint del splash del SPA — el reloj del dwell corre desde aquí.
  const shownAtRef = useRef(active ? Date.now() : null);

  /* #457 (P14) — QUITAR LA PANTALLA NATIVA CUANDO ÉSTA YA ESTÁ PINTADA DEBAJO.
   *
   * Con `launchAutoHide: false` (ver `capacitor.config.json`), el splash nativo
   * — que es el MISMO storyboard: mismo fondo, mismo logo, mismo tamaño y mismo
   * sitio — se queda encima del WKWebView hasta que alguien pide quitarlo. Eso
   * tapa el hueco de P14: el `#111111` liso, sin logo, que se veía entre que iOS
   * retiraba su pantalla de arranque y React montaba ésta.
   *
   * SE PIDE DESPUÉS DEL PRIMER PINTADO, no en el montaje: el efecto corre tras
   * el commit, y el `requestAnimationFrame` espera al cuadro siguiente, que es
   * el primero donde este overlay ya está en pantalla. Pedirlo antes reabriría
   * el hueco, esta vez de un cuadro.
   *
   * NO TOCA LA TRANSICIÓN: la espera (1,400) y el fundido (600) de abajo quedan
   * exactamente como estaban. Esto sólo retira una capa nativa que el usuario no
   * puede distinguir de ésta.
   *
   * En web es inerte (el gate vive dentro de `lib/splashNativo.js`), y el
   * vigilante de `index.js` lo vuelve a pedir a los 4 s por si este componente
   * no llegara a montarse. Los dos `hide` son idempotentes.
   */
  useEffect(() => {
    if (!active) return undefined;
    const id = requestAnimationFrame(() => ocultarSplashNativo());
    return () => cancelAnimationFrame(id);
  }, [active]);

  useEffect(() => {
    if (active) {
      if (shownAtRef.current === null) shownAtRef.current = Date.now();
      setPhase('shown');
      return undefined;
    }
    if (shownAtRef.current === null) return undefined; // nunca se mostró (rutas web instantáneas)

    // total visible = max(boot real, mínimo): solo se espera lo que FALTE.
    const elapsed = Date.now() - shownAtRef.current;
    const holdMs = Math.max(0, BOOT_SPLASH_MIN_MS - elapsed);
    let fadeTimer;
    const holdTimer = setTimeout(() => {
      setPhase('fading');
      fadeTimer = setTimeout(() => setPhase('hidden'), BOOT_FADE_MS);
    }, holdMs);
    return () => { clearTimeout(holdTimer); clearTimeout(fadeTimer); };
  }, [active]);

  if (phase === 'hidden') return null;
  const fading = phase === 'fading';

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center pointer-events-none"
      style={{
        backgroundColor: BOOT_BACKGROUND,
        opacity: fading ? 0 : 1,
        transition: `opacity ${BOOT_FADE_MS}ms ease-in-out`,
      }}
      aria-hidden="true"
      data-testid="boot-splash"
    >
      <img
        src={logo}
        alt=""
        className="w-24 h-24 md:w-28 md:h-28"
        // Micro-detalle del fade: el logo crece apenas (1.0→1.02) mientras se
        // desvanece — da sensación de "abrirse" hacia la app. Sutil a propósito.
        style={{
          transform: fading ? 'scale(1.02)' : 'scale(1)',
          transition: `transform ${BOOT_FADE_MS}ms ease-in-out`,
        }}
        data-testid="boot-splash-logo"
      />
    </div>
  );
}
